import React, { useState } from 'react'
import Form from 'react-bootstrap/cjs/Form'
import Button from 'react-bootstrap/cjs/Button'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { useHistory, useLocation } from 'react-router-dom'
import colors from '../../../constants/colors'
import useResponsive from '../../../hooks/useResponsive'
import { showErrorNotification, showNotification } from "../../notifications/notificationsSlice"
import auth from "../../../utils/auth"

const S = {
    FormWrapper: styled.div`
        flex: 1;
        overflow: auto;
        padding: 20px;
        max-width: 400px;
        margin: 0 auto;
    `,
    Label: styled(Form.Label)`
        font-size: 12px;
        font-weight: 500;
        color: ${colors.label};
    `,
    Button: styled(Button)<{ $isMobile: boolean }>`
        max-width: ${({ $isMobile }) => ($isMobile ? '100%' : '300px')};
        width: ${({ $isMobile }) => ($isMobile ? '100%' : 'auto')};
        margin-top: 16px;
    `,
    Title: styled.h2`
        color: ${colors.darkTitle};
        margin-bottom: 24px;
        text-align: center;
    `,
    Description: styled.p`
        color: ${colors.grayDescription};
        margin-bottom: 24px;
        text-align: center;
    `,
    ButtonWrapper: styled.div`
        display: flex;
        flex-direction: row;
        justify-content: center;
        margin-top: 24px;
    `,
}

const ResetPasswordForm = () => {
    const dispatch = useDispatch()
    const history = useHistory()
    const {search} = useLocation()
    const params = Object.fromEntries(new URLSearchParams(search)) as { uid: string; token: string }
    const { isMobile } = useResponsive()

    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({})

    const validateForm = () => {
        const newErrors: { password?: string; confirmPassword?: string } = {}

        if (!password) {
            newErrors.password = 'Password is required'
        } else if (password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters long'
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password'
        } else if (password !== confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match'
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!validateForm()) {
            return
        }

        setIsLoading(true)

        try {
            await auth.confirmResetPassword(params.uid, params.token, password)
            dispatch(showNotification({
                body: 'Password has been reset successfully. You can now sign in with your new password.',
                type: 'success'
            }))
            history.push(`/${localStorage.getItem('lastVisitedEventSlug') || ''}/enterevent/`)
        } catch (error) {
            dispatch(showErrorNotification(
                error instanceof Error ? error.message : 'Failed to reset password. The link may be invalid or expired.'
            ))
        } finally {
            setIsLoading(false)
        }
    }

    if (!params.uid || !params.token) {
        return (
            <S.FormWrapper>
                <S.Title>Invalid Reset Link</S.Title>
                <S.Description>
                    The password reset link is invalid or malformed. Please request a new password reset.
                </S.Description>
            </S.FormWrapper>
        )
    }

    return (
        <S.FormWrapper>
            <S.Title>Reset Your Password</S.Title>
            <S.Description>
                Enter your new password below to complete the reset process.
            </S.Description>

            <Form noValidate onSubmit={handleSubmit}>
                <Form.Group>
                    <S.Label>New Password *</S.Label>
                    <Form.Control
                        type="password"
                        placeholder="Enter your new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        isInvalid={!!errors.password}
                        required
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.password}
                    </Form.Control.Feedback>
                </Form.Group>

                <Form.Group style={{ marginTop: '16px' }}>
                    <S.Label>Confirm New Password *</S.Label>
                    <Form.Control
                        type="password"
                        placeholder="Confirm your new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        isInvalid={!!errors.confirmPassword}
                        required
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.confirmPassword}
                    </Form.Control.Feedback>
                </Form.Group>

                <S.ButtonWrapper>
                    <S.Button
                        type="submit"
                        disabled={isLoading}
                        $isMobile={isMobile}
                    >
                        {isLoading ? 'Resetting Password...' : 'Reset Password'}
                    </S.Button>
                </S.ButtonWrapper>
            </Form>
        </S.FormWrapper>
    )
}

export default ResetPasswordForm
